import { nativeBridge } from '../secretarioNative.js';

/**
 * Áudio da conversa ao vivo: microfone em PCM 16 kHz (pedaços de 40 ms) e
 * reprodução da voz do Secretário em PCM 24 kHz, com fila contínua e corte
 * imediato quando a pessoa interrompe. Os dois expõem o volume do momento,
 * que anima o personagem e o fundo sem passar pelo React.
 */

export type Mic = {
  stop(): void;
  setMuted(muted: boolean): void;
  /** Volume recente da voz da pessoa, de 0 a 1. */
  level(): number;
};

/** Áudio falso só para desenvolvimento: `?microfone=arquivo` toca um WAV no lugar do microfone. */
async function fakeStream(context: AudioContext): Promise<MediaStream> {
  const response = await fetch('/voice/dev-question.wav');
  const buffer = await context.decodeAudioData(await response.arrayBuffer());
  const source = context.createBufferSource();
  source.buffer = buffer;
  const destination = context.createMediaStreamDestination();
  source.connect(destination);
  source.start(context.currentTime + 1.2);
  return destination.stream;
}

export async function openMic(onChunk: (pcm: ArrayBuffer) => void): Promise<Mic> {
  const context = new AudioContext({ sampleRate: 16000 });
  const fake =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get('microfone') === 'arquivo';
  const stream = fake
    ? await fakeStream(context)
    : await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
  await context.audioWorklet.addModule('/voice/pcm-capture.js');
  const source = context.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(context, 'pcm-capture');
  let current = 0;
  node.port.onmessage = (event: MessageEvent<{ chunk: ArrayBuffer; level: number }>) => {
    // Sobe rápido e desce devagar: a tela não pisca entre sílabas.
    const level = Math.min(1, event.data.level * 6);
    current = level > current ? level : current * 0.85 + level * 0.15;
    onChunk(event.data.chunk);
  };
  source.connect(node);
  if (context.state === 'suspended') await context.resume();
  // Microfone aberto põe o Android em modo de chamada: a voz segue o volume de mídia.
  void nativeBridge.volumeSync(true);
  return {
    stop: () => {
      void nativeBridge.volumeSync(false);
      node.port.onmessage = null;
      source.disconnect();
      node.disconnect();
      for (const track of stream.getTracks()) track.stop();
      void context.close().catch(() => {});
    },
    setMuted: (muted) => node.port.postMessage({ muted }),
    level: () => current,
  };
}

/** Voz do Secretário: fila de trechos PCM 24 kHz tocados em sequência, sem buracos. */
export class VoicePlayer {
  private readonly context = new AudioContext({ sampleRate: 24000 });
  private readonly analyser: AnalyserNode;
  private readonly samples: Float32Array<ArrayBuffer>;
  private readonly spectrum: Uint8Array<ArrayBuffer>;
  private readonly sources = new Set<AudioBufferSourceNode>();
  private nextAt = 0;
  private smooth = 0;

  constructor() {
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.connect(this.context.destination);
    this.samples = new Float32Array(this.analyser.fftSize);
    this.spectrum = new Uint8Array(this.analyser.frequencyBinCount);
  }

  /** O navegador só libera som depois de um gesto: chamar no toque que abriu a conversa. */
  unlock(): void {
    if (this.context.state === 'suspended') void this.context.resume();
  }

  push(base64: string): void {
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.byteLength / 2));
    if (pcm.length === 0) return;
    const buffer = this.context.createBuffer(1, pcm.length, 24000);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i += 1) channel[i] = (pcm[i] ?? 0) / 0x8000;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyser);
    // Pequena folga no começo de cada fala contra falhas de rede.
    const startAt = Math.max(this.nextAt, this.context.currentTime + 0.06);
    source.start(startAt);
    this.nextAt = startAt + buffer.duration;
    this.sources.add(source);
    source.onended = () => this.sources.delete(source);
  }

  /** A pessoa interrompeu: para tudo na hora. */
  flush(): void {
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // já tinha terminado
      }
    }
    this.sources.clear();
    this.nextAt = 0;
  }

  speaking(): boolean {
    return this.context.currentTime < this.nextAt - 0.02;
  }

  /** Volume do que está tocando agora, de 0 a 1 (boca e fundo acompanham). */
  level(): number {
    if (!this.speaking()) {
      this.smooth *= 0.8;
      return this.smooth;
    }
    this.analyser.getFloatTimeDomainData(this.samples);
    let sum = 0;
    for (const value of this.samples) sum += value * value;
    const level = Math.min(1, Math.sqrt(sum / this.samples.length) * 5);
    this.smooth =
      level > this.smooth ? level * 0.7 + this.smooth * 0.3 : this.smooth * 0.75 + level * 0.25;
    return this.smooth;
  }

  /**
   * Amostra crua do que está tocando agora (para a sincronia labial): volume e
   * energia em três faixas de frequência. Sem fala, devolve null.
   */
  sample(): { rms: number; low: number; mid: number; high: number } | null {
    if (!this.speaking()) return null;
    this.analyser.getFloatTimeDomainData(this.samples);
    let sum = 0;
    for (const value of this.samples) sum += value * value;
    const rms = Math.min(1, Math.sqrt(sum / this.samples.length) * 4);
    this.analyser.getByteFrequencyData(this.spectrum);
    const hz = this.context.sampleRate / this.analyser.fftSize;
    const band = (from: number, to: number) => {
      let total = 0;
      let count = 0;
      for (
        let i = Math.floor(from / hz);
        i <= Math.min(this.spectrum.length - 1, Math.ceil(to / hz));
        i += 1
      ) {
        total += this.spectrum[i] ?? 0;
        count += 1;
      }
      return count ? total / count / 255 : 0;
    };
    return { rms, low: band(250, 900), mid: band(900, 2000), high: band(2000, 4500) };
  }

  close(): void {
    this.flush();
    void this.context.close().catch(() => {});
  }
}

/** ArrayBuffer em base64, sem estourar a pilha em pedaços grandes. */
export function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
